import styled from 'styled-components';
import React from 'react';
import { Experience } from '@/webapi/use-experience-api';
import { PriceTestProductSummary } from '@/features/details/price-test-reports/api';
import {
  format,
  getUplift,
  getVariantName,
  SummaryBody,
  SummaryHeader,
} from '@/features/details/price-test-reports/shared';
import { MetricKind } from '@/webapi/models';
import { GridValue } from '@/features/details/price-test-reports/desktop-stats';

export function MobileDailyReport({
  experience,
  p,
}: {
  experience?: Experience;
  p?: PriceTestProductSummary;
}) {
  return (
    <Wrapper>
      <List>
        <HeaderRow>Variation</HeaderRow>
        <SummaryHeader>Sessions</SummaryHeader>
        <SummaryHeader>CR</SummaryHeader>
        <SummaryHeader>PSV</SummaryHeader>
        <SummaryHeader>Units Sold</SummaryHeader>
        <SummaryHeader>Transactions</SummaryHeader>
        <SummaryHeader>PDV %</SummaryHeader>
        <SummaryHeader>Profit</SummaryHeader>
        <SummaryHeader>Profit %</SummaryHeader>
        <SummaryHeader>PPS</SummaryHeader>
        <SummaryHeader>Returns</SummaryHeader>
        <SummaryHeader>Returns %</SummaryHeader>
        <SummaryHeader>Revenue</SummaryHeader>
        <SummaryHeader>IPP</SummaryHeader>
        <SummaryHeader>ATC %</SummaryHeader>
        <SummaryHeader>OTW</SummaryHeader>
      </List>
      <Scroller>
        {p?.variants.map((v) => (
          <Card key={v.useCaseVariant}>
            <SummaryBody>
              <span>{getVariantName(experience, v)}</span>
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_SESSIONS}
                value={format(v.sessions)}
                uplift={getUplift(v, p, (v) => v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_CR}
                value={format(v.transactions / v.sessions, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.transactions / v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_PSV}
                value={format(v.revenue / v.sessions, `$`, ``)}
                uplift={getUplift(v, p, (v) => v.revenue / v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.ITEMS_SOLD}
                value={format(v.itemsSold)}
                uplift={getUplift(v, p, (v) => v.itemsSold)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.TRANSACTIONS}
                value={format(v.transactions)}
                uplift={getUplift(v, p, (v) => v.transactions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_DETAILS_VIEWS_RATE}
                value={format(v.pdv, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.pdv)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_PROFIT}
                value={format(v.profit, `$`, ``)}
                uplift={getUplift(v, p, (v) => v.profit)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PROFIT_PERCENT}
                value={format(v.profit, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.profit / v.revenue)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_PROFIT_PER_SESSION}
                value={format(v.profit / v.sessions, `$`, ``)}
                uplift={getUplift(v, p, (v) => v.profit / v.sessions)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_RETURNS}
                value={format(v.returns)}
                uplift={getUplift(v, p, (v) => v.returns)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_RETURN_PERCENT}
                value={format((v.returns / v.itemsSold) * 100, ``, `%`)}
                uplift={getUplift(v, p, (v) => (v.returns / v.itemsSold) * 100)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_REVENUE}
                value={format(v.revenue, `$`, ``)}
                uplift={getUplift(v, p, (v) => v.revenue)}
              />
            </SummaryBody>
            <SummaryBody>
              <GridValue
                variation={v}
                pid={p.pid}
                metric={MetricKind.PRODUCT_ATC_RATE}
                value={format(v.atcRate, ``, `%`)}
                uplift={getUplift(v, p, (v) => v.atcRate)}
              />
            </SummaryBody>
            <SummaryBody>
              <span>{format(v.otw * 100, ``, `%`)}</span>
            </SummaryBody>
          </Card>
        ))}
      </Scroller>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;

  && > *:first-child {
    flex: 0 0 auto;
  }

  padding-bottom: 1rem;
  padding-right: 1rem;
`;

const Scroller = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: scroll;
  gap: 1.5rem;

  width: 100%;
  padding: 10px;
  margin: -10px;

  mask-image: linear-gradient(
    var(--mask-direction, to right),
    hsl(0 0% 0% / 0),
    rgb(248 249 251 / 1) 5%,
    rgb(248 249 251 / 1) 95%,
    hsl(0 0% 0% / 0)
  );

  && > * {
    flex: 1 0 auto;
  }
`;

const List = styled.div`
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2rem;

  && > * {
    min-height: 1.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const Card = styled(List)`
  && {
    background: white;
    border-radius: 0.8rem;
    justify-content: center;
    align-items: center;
    box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.15),
      0 4px 11px 0 rgba(151, 210, 206, 0.22);
  }
`;

const HeaderRow = styled.span`
  text-align: start;
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  color: #a6afb8;

  &&.bold {
    font-weight: 700;
    color: black;
  }
`;
